import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut, 
  onAuthStateChanged,
  fetchSignInMethodsForEmail,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import app from '../config/firebase';

// Inicializamos auth directamente en este módulo para evitar referencias circulares
const auth = getAuth(app);

// Lista de emails autorizados para acceder al sistema administrativo
const AUTHORIZED_EMAILS = [
  'sebastianpolan@gmail.com',
  'belkys.hernandez17@gmail.com',
  'belkys.hernandez@gmail.com'
  // Agrega aquí los emails que deben tener acceso
];

// Configuramos el proveedor de Google
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});
// Añadimos scopes básicos para obtener información del perfil
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Función para verificar si un email está autorizado
const isEmailAuthorized = (email) => {
  return AUTHORIZED_EMAILS.includes(email?.toLowerCase());
};

// Función para verificar métodos de inicio de sesión disponibles
const checkSignInMethods = async (email) => {
  try {
    const methods = await fetchSignInMethodsForEmail(auth, email);
    return methods;
  } catch (error) {
    console.error('Error al verificar métodos de inicio de sesión:', error);
    return [];
  }
};

// Función para registrar un usuario con email/contraseña (útil para administradores)
const registerWithEmailPassword = async (email, password, displayName = 'Administrador') => {
  try {
    // Verificar si ya existe
    const methods = await checkSignInMethods(email);
    if (methods.length > 0) {
      return { success: false, error: 'El usuario ya existe. Usa el método de inicio de sesión correspondiente.' };
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Actualizar el perfil con el nombre
    await updateProfile(userCredential.user, {
      displayName: displayName
    });

    console.log('Usuario registrado exitosamente con email/contraseña');
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('Error al registrar usuario:', error);
    let errorMessage = 'Error al registrar usuario';
    
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'Este email ya está en uso';
        break;
      case 'auth/weak-password':
        errorMessage = 'La contraseña es muy débil';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Email inválido';
        break;
      default:
        errorMessage = error.message || 'Error al registrar usuario';
    }
    
    return { success: false, error: errorMessage };
  }
};

// Crear contexto de autenticación
const AuthContext = createContext();

// Hook personalizado para acceder al contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificamos que auth esté disponible antes de usarlo
    if (!auth) {
      console.error('Auth no está disponible');
      setLoading(false);
      return;
    }

    console.log('Inicializando auth listener');
    // Escuchar cambios en el estado de autenticación de Firebase
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Verificar si el email está autorizado
        if (!isEmailAuthorized(firebaseUser.email)) {
          console.warn('Email no autorizado:', firebaseUser.email);
          // Cerrar sesión inmediatamente si no está autorizado
          await signOut(auth);
          setUser(null);
          localStorage.removeItem('user');
          setLoading(false);
          return;
        }

        // Usuario autenticado y autorizado
        const userData = {
          id: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName || 'Dr. Veterinario',
          role: 'admin'
        };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        // Usuario no autenticado
        setUser(null);
        localStorage.removeItem('user');
      }
      setLoading(false);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      
      // Verificar si el email está autorizado antes de intentar el login
      if (!isEmailAuthorized(email)) {
        setLoading(false);
        return { 
          success: false, 
          error: 'Tu email no está autorizado para acceder al sistema administrativo. Contacta al administrador.' 
        };
      }
      
      await signInWithEmailAndPassword(auth, email, password);
      
      // Firebase automáticamente actualizará el estado a través de onAuthStateChanged
      return { success: true };
    } catch (error) {
      setLoading(false);
      console.error('Error de autenticación:', error);
      
      // Verificar métodos disponibles para este email
      let errorMessage = 'Error de conexión';
      
      // Si el usuario no fue encontrado, verificar si existe con otros métodos
      if (error.code === 'auth/user-not-found') {
        const methods = await checkSignInMethods(email);
        if (methods.includes('google.com')) {
          errorMessage = 'Esta cuenta está registrada con Google. Usa "Continuar con Google" para iniciar sesión.';
        } else {
          errorMessage = 'No se encontró una cuenta con este email';
        }
      } else {
        switch (error.code) {
          case 'auth/wrong-password':
            // Verificar si también puede usar Google
            const methods = await checkSignInMethods(email);
            if (methods.includes('google.com')) {
              errorMessage = 'Contraseña incorrecta. También puedes usar "Continuar con Google".';
            } else {
              errorMessage = 'Contraseña incorrecta';
            }
            break;
          case 'auth/invalid-email':
            errorMessage = 'Email inválido';
            break;
          case 'auth/user-disabled':
            errorMessage = 'Esta cuenta ha sido deshabilitada';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Demasiados intentos. Intenta más tarde';
            break;
          case 'auth/invalid-credential':
            errorMessage = 'Credenciales inválidas. Verifica tu email y contraseña';
            break;
          default:
            errorMessage = error.message || 'Error al iniciar sesión';
        }
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      
      // Verificar si el email está autorizado
      if (!isEmailAuthorized(result.user.email)) {
        // Si no está autorizado, cerrar sesión inmediatamente
        await signOut(auth);
        setLoading(false);
        return { 
          success: false, 
          error: 'Tu email no está autorizado para acceder al sistema administrativo. Contacta al administrador.' 
        };
      }
      
      // Email autorizado - Firebase automáticamente actualizará el estado a través de onAuthStateChanged
      return { success: true };
    } catch (error) {
      setLoading(false);
      console.error('Error de autenticación con Google:', error);
      
      let errorMessage = 'Error al iniciar sesión con Google';
      
      switch (error.code) {
        case 'auth/popup-blocked':
          errorMessage = 'El popup fue bloqueado. Permite popups para este sitio';
          break;
        case 'auth/popup-closed-by-user':
          errorMessage = 'Ventana cerrada por el usuario';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = 'Solicitud cancelada';
          break;
        default:
          errorMessage = error.message || 'Error al iniciar sesión con Google';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      // Firebase automáticamente actualizará el estado a través de onAuthStateChanged
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const value = {
    user,
    login,
    loginWithGoogle,
    logout,
    loading,
    isAuthenticated: !!user,
    checkSignInMethods,
    registerWithEmailPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
